<!-- eslint-disable vue/require-prop-type-constructor -->
<script setup>
import {
    computed, defineEmits, ref, onMounted,
} from "vue";
import userMixin from "../mixins/userMixins";
import { getUpcomingEventsAmount } from "@/components/methods";
import SummaryTicketsAndPrice from "./summaries/SummaryTicketsAndPrice.vue";
import SummaryBookingSettings from "./summaries/SummaryBookingSettings.vue";

defineEmits(["editClick"]);

const props = defineProps({
    activity: {
        eventType: "",
        ages: [],
        description: "",
        bookingEmail: "",
        bookingLink: "",
        bookingType: "",
    },
    // eslint-disable-next-line vue/require-prop-type-constructor
    mainCategory: "",
    // eslint-disable-next-line vue/require-prop-type-constructor
    secondaryCategory: "",
    // eslint-disable-next-line vue/require-prop-type-constructor
    ages: "",
    addHoliday: String,
    img: String,
    dates: Object,
    times: Object,
    holidays: Array,
    price: Object,
    // eslint-disable-next-line vue/require-prop-type-constructor
    forceShowHolidays: false,
    hideDates: {
        type: Boolean,
        default: false,
    },
    priceV2: Object,
    allEventsAmount: Number,
});

const isSuperAdmin = ref(null);

const repeatType = computed(() => (props.dates.repeat === "month-d" || props.dates.repeat === "month-w"
    ? "month"
    : props.dates.repeat));

function getDescription() {
    if (props.activity?.description?.length > 20) {
        return `${props.activity?.description?.slice(0, 20)} ...`;
    }

    return props.activity?.description;
}

onMounted(async () => {
    isSuperAdmin.value = await userMixin.methods.isSuperAdmin();
});

const getAllEventsAmount = computed(() => props.allEventsAmount || getUpcomingEventsAmount(
    new Date(props.dates.startRaw),
    new Date(props.dates.endRaw),
    props.dates.repeat,
    props.holidays,
));

const getEventType = computed(() => props.activity?.eventType?.name || props.activity?.eventType);

const getLowerCaseEventType = computed(() => (!!getEventType.value && !!getEventType.value ? getEventType.value.toLowerCase() : ""));
</script>

<template>
    <div class="d-flex flex-column summary-block" data-test="summary-detail">
        <div class="d-flex flex-row justify-content-between">
            <div class="simple-event-title" data-test="summary-detail-title">
                {{ getEventType }} details
            </div>
            <div @click="$emit('editClick', 'details')" class="card-text cursor-pointer"
                 data-test="summary-detail-btn-edit">Edit
            </div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-type">
            <div>Event type</div>
            <div class="event-data">{{ getEventType }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-ages">
            <div>Suitable ages</div>
            <div class="event-data">{{ ages }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-name">
            <div>{{ getEventType }} name</div>
            <div class="event-data" v-html="activity.name"></div>
        </div>

        <div v-if="isSuperAdmin && !!activity.bookingLink" class="d-flex flex-row justify-content-between mt-3 pl-3"
             data-test="summary-detail-name">
            <div>Affiliate link</div>
            <div class="event-data" v-html="activity.bookingLink"></div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-desc">
            <div>{{ getEventType }} description</div>
            <div class="event-data" v-html="getDescription()">
            </div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-picture">
            <div>{{ getEventType }} picture</div>
            <div>
                <img class="small-img" :src="img"/>
            </div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-category-primary">
            <div>Main category</div>
            <div class="event-data">{{ mainCategory }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-category-secondary">
            <div>Other categories</div>
            <div class="event-data">{{ secondaryCategory }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-email">
            <div>Booking email</div>
            <div class="event-data">{{ activity.bookingEmail }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-detail-attendance">
            <div>Attendance</div>
            <div class="event-data">
                {{
                    activity.bookingType !== ""
                        ? activity.bookingType === "required"
                            ? "Required"
                            : "Drop in"
                        : ""
                }}
            </div>
        </div>
    </div>

    <div v-if="!hideDates" class="d-flex flex-column mt-3r" data-test="summary-datetime">
        <div class="d-flex flex-row justify-content-between">
            <div class="simple-event-title" data-test="summary-datetime-title">Date and time</div>
            <div @click="$emit('editClick', 'date-time')" class="card-text cursor-pointer"
                 data-test="summary-datetime-edit">
                Edit
            </div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-datetime-start">
            <div>{{ getEventType }} start date</div>
            <div class="event-data">{{ dates.start }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-datetime-repeat">
            <div>Repeat</div>
            <div v-if="repeatType !== 'none' && repeatType !== 'no-repeat'" class="event-data">
                Every {{ repeatType }}
            </div>
            <div v-else class="event-data">Single day</div>
        </div>

        <div
            v-if="repeatType !== 'none' && repeatType !== 'no-repeat'"
            class="d-flex flex-row justify-content-between mt-3 pl-3"
        >
            <div>Repeat until</div>
            <div class="event-data">{{ dates.end }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-datetime-start-date">
            <div>Start time</div>
            <div class="event-data">{{ times.start }}</div>
        </div>

        <div class="d-flex flex-row justify-content-between mt-3 pl-3" data-test="summary-datetime-end-date">
            <div>End time</div>
            <div class="event-data">{{ times.end }}</div>
        </div>
    </div>

    <div
        v-if="
            ((holidays.length > 0 && addHoliday === 'yes') || forceShowHolidays) &&
                repeatType !== 'none' &&
                repeatType !== 'no-repeat'
        "
        class="d-flex flex-column mt-3r"
        data-test="summary-holiday"
    >
        <div class="d-flex flex-row justify-content-between">
            <div class="simple-event-title" data-test="summary-holiday-title">Holiday</div>
            <div
                v-if="forceShowHolidays"
                @click="$emit('editClick', 'holidays')"
                class="card-text cursor-pointer"
                data-test="summary-holiday-edit"
            >
                Edit
            </div>
        </div>

        <div v-if="forceShowHolidays && holidays.length === 0" class="pl-3 mt-3 event-data">None</div>

        <div
            v-else
            v-for="(holiday, idx) in holidays"
            :key="idx"
            class="d-flex flex-row justify-content-between mt-3 pl-3 event-data"
        >
            {{ holiday.start }} {{ !holiday.oneDay ? " - " + holiday.end : "" }}
        </div>
    </div>
    <SummaryTicketsAndPrice
        :ticket-and-pricing-data="priceV2"
        :event-type="getLowerCaseEventType"
        :all-events-amount="getAllEventsAmount"
        @edit-click="($event) => $emit('editClick', $event)"
    />
    <SummaryBookingSettings v-if="priceV2?.sellingTicket"/>
</template>
<style scoped>
.event-data {
    font-weight: 400;
    color: #636066;
}

.mt-3r {
    margin-top: 3rem;
}

.see-all {
    font-weight: 600;
    font-size: 14px;
    line-height: 21px;
    color: #260045;
    text-align: end;
    margin-top: 1rem;
    cursor: pointer;
}
</style>
